/* The difference between a bug and a blank page.
 *
 * React unmounts the whole tree when a render or an effect throws. Without a
 * boundary that means one bad field in one block — a null where a string was
 * expected, a `scrollIntoView` that does not exist in some browser — takes the
 * entire app to a white screen with nothing on it and no way back.
 *
 * Three layers, from widest to narrowest:
 *
 *   · **ErrorBoundary**, around everything: the floor. If even the shell fails,
 *     say so and offer a reload.
 *   · **ScreenBoundary**, around the screens: a crash in the month view leaves
 *     the sidebar, the New button and every other screen working, and moving
 *     to another screen clears it. This is the one people will actually meet.
 *   · **QuietBoundary**, around small extras (the New menu): if it fails it
 *     disappears rather than taking anything with it.
 *
 * Every caught error is also reported to the server (`/api/client-errors`), so
 * a crash on somebody's phone shows up in the admin console instead of only in
 * a console nobody opened.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { reportClientError } from './ui/report';

interface Props { children: ReactNode }
interface State { error: Error | null }

export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[amon] render failed', error, info.componentStack);
    reportClientError(error, { where: 'app', componentStack: info.componentStack ?? undefined });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="boot" role="alert">
        <div className="brand-mark">A</div>
        <h1>Amon hit a bug</h1>
        <p className="muted" style={{ maxWidth: '44ch', textAlign: 'center' }}>
          Something in the app failed while drawing this screen. Your plan is safe —
          nothing here writes to it.
        </p>
        <p className="muted small" style={{ maxWidth: '52ch', textAlign: 'center' }}>
          <code>{error.message}</code>
        </p>
        <button className="btn btn-primary" onClick={() => location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}

/* One screen's failure, contained to that screen. `resetKey` is the route: the
   moment somebody goes somewhere else, the error is forgotten and the new
   screen gets its own chance. */
export class ScreenBoundary extends Component<Props & { resetKey: string }, State & { key: string }> {
  override state = { error: null as Error | null, key: this.props.resetKey };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  static getDerivedStateFromProps(props: { resetKey: string }, state: { key: string; error: Error | null }) {
    return props.resetKey !== state.key ? { error: null, key: props.resetKey } : null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[amon] screen failed', error, info.componentStack);
    reportClientError(error, { where: `screen:${this.props.resetKey}`, componentStack: info.componentStack ?? undefined });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="screen">
        <div className="notice notice-bad screen-crash" role="alert">
          <p><strong>This screen hit a bug.</strong></p>
          <p className="muted small">
            The rest of Amon still works, and nothing was lost. It has been reported.
          </p>
          <p className="muted small"><code>{error.message}</code></p>
          <div className="inline">
            <button className="btn btn-primary btn-sm" onClick={() => this.setState({ error: null })}>
              Try again
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => location.assign('/day')}>
              Go to today
            </button>
          </div>
        </div>
      </div>
    );
  }
}

/* For extras that should simply vanish if they break. */
export class QuietBoundary extends Component<Props & { name: string }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[amon] ${this.props.name} failed`, error);
    reportClientError(error, { where: this.props.name, componentStack: info.componentStack ?? undefined });
  }

  override render(): ReactNode {
    return this.state.error ? null : this.props.children;
  }
}
